// The scoring engine — pure functions shared by the web app (live projections)
// and the Cloud Functions grader, so a card is always scored by identical code.
//
// Football is a Build-Your-Card bet-builder. Each fixture you enter is a CARD:
//   • a 1X2 `outcome` ANCHOR (the qualifier), and
//   • 0..CONFIG.COMBO.MAX_LEGS multiplier LEGS the player chose (graded via the
//     market registry's `gradeLeg`).
// The card scores (see `cardScore`):
//   anchor WRONG → −BASE_POINTS                       (a real loss)
//   anchor RIGHT → round( BASE_POINTS × Π legFactor ) (correct legs multiply up,
//                                                      wrong legs bite down)
//   void fixture → excluded (0, never −BASE).
// Round score = the sum of every card. There is no confidence lever.
import { CONFIG } from './config.js';
import { gradeLeg } from './markets.js';
/**
 * Was this a genuine goal credited to its scorer — i.e. a Goal event that changed
 * the score and is NOT an own goal (penalties DO count)? The single rule both the
 * scorer leg's grading and the "your pick scored" live push share, so the two
 * never disagree about who scored.
 */
export function isGenuineGoalEvent(e) {
    return e.type === 'GOAL' && e.scored === true && (e.detail ?? '').toLowerCase() !== 'own goal';
}
/**
 * The provider player ids of everyone who scored a genuine goal in a match, in
 * order of first goal, de-duplicated. Drives the "anytime scorer" result. Events
 * without a player id (provider gap) are skipped — a scorer leg simply misses.
 */
export function scorerIdsFromEvents(events) {
    if (!events || events.length === 0)
        return [];
    const ids = [];
    const seen = new Set();
    for (const e of events) {
        if (!isGenuineGoalEvent(e))
            continue;
        const id = e.playerId != null ? String(e.playerId) : '';
        if (!id || seen.has(id))
            continue;
        seen.add(id);
        ids.push(id);
    }
    return ids;
}
/** Was there a red card shown in this event timeline? Powers the `redCard` leg's
 *  result field. A CARD event whose detail names a red (straight or second yellow). */
export function redCardFromEvents(events) {
    for (const e of events ?? []) {
        if (e.type !== 'CARD')
            continue;
        if ((e.detail ?? '').toLowerCase().includes('red'))
            return true;
    }
    return false;
}
/** Total cards (yellow + red) per side from an event timeline — the fallback for the
 *  `mostCards` leg where no `/fixtures/statistics` block is available (offline replay,
 *  the simulate script). Each card (incl. a second yellow) is its own CARD event. */
export function cardCountsFromEvents(events) {
    let home = 0;
    let away = 0;
    for (const e of events ?? []) {
        if (e.type !== 'CARD')
            continue;
        if (e.side === 'HOME')
            home++;
        else if (e.side === 'AWAY')
            away++;
    }
    return { home, away };
}
/** The coverage-gated card extras (`redCard`, per-side card counts) read straight off
 *  an event timeline — the convenience the offline/demo result builders use so their
 *  results carry the same fields the live provider path fills. */
export function extrasFromEvents(events) {
    const c = cardCountsFromEvents(events);
    return { redCard: redCardFromEvents(events), homeCards: c.home, awayCards: c.away };
}
/**
 * The half-time score implied by an event timeline: the running scoreline at the
 * last event of the first half (`elapsed <= 45`, which includes first-half added
 * time). Used to synthesise a half-time score where the provider doesn't hand one
 * directly (offline replay + the simulate script); the live API uses the provider's
 * own `score.halftime`. Order-robust (each event carries its own running score), so
 * it tolerates an unsorted feed. Returns 0–0 when no first-half event exists, so the
 * CALLER must only trust it once the first half is actually complete.
 */
export function halftimeFromEvents(events) {
    let ht = { home: 0, away: 0 };
    let best = -1;
    for (const e of events ?? []) {
        if (e.elapsed > 45)
            continue; // second-half (46'+) events never move the half-time score
        const within = e.elapsed * 100 + (e.extra ?? 0); // sort key within the half (incl. stoppage)
        if (within >= best) {
            best = within;
            ht = { home: e.homeGoals, away: e.awayGoals };
        }
    }
    return ht;
}
/**
 * Derive the canonical result from a final score, so settlement is deterministic.
 * Goals must be non-negative integers. `scorerIds` (the genuine goalscorers) and the
 * optional `extras` (red card / per-side card counts) are carried onto the result so
 * the events/stats legs grade from the same canonical place. Missing extras stay null
 * so those legs read as ungradeable (excluded), never wrong.
 */
export function deriveResult(fixtureId, homeGoals, awayGoals, scorerIds = [], halftime, extras) {
    const h = Math.max(0, Math.trunc(homeGoals));
    const a = Math.max(0, Math.trunc(awayGoals));
    const outcome = h > a ? 'HOME' : h < a ? 'AWAY' : 'DRAW';
    const btts = h > 0 && a > 0 ? 'YES' : 'NO';
    const totalGoals = h + a > CONFIG.TOTAL_GOALS_LINE ? 'OVER' : 'UNDER';
    const exactScore = `${h}-${a}`;
    // Half-time goals can't exceed full-time; clamp defensively so a provider glitch
    // (or extra-time being folded in oddly) never yields a negative second half.
    const ht = halftime && Number.isFinite(halftime.home) && Number.isFinite(halftime.away)
        ? { home: Math.min(h, Math.max(0, Math.trunc(halftime.home))), away: Math.min(a, Math.max(0, Math.trunc(halftime.away))) }
        : null;
    return {
        fixtureId,
        homeGoals: h,
        awayGoals: a,
        halftimeHome: ht ? ht.home : null,
        halftimeAway: ht ? ht.away : null,
        outcome,
        btts,
        totalGoals,
        exactScore,
        scorerIds: [...scorerIds],
        redCard: extras?.redCard ?? null,
        homeCards: extras?.homeCards ?? null,
        awayCards: extras?.awayCards ?? null,
        homeCorners: extras?.homeCorners ?? null,
        awayCorners: extras?.awayCorners ?? null,
        status: 'SETTLED'
    };
}
/** A void result placeholder — a fixture that won't be played within the round. */
export function voidResult(fixtureId) {
    return {
        fixtureId,
        homeGoals: 0,
        awayGoals: 0,
        halftimeHome: null,
        halftimeAway: null,
        outcome: 'DRAW',
        btts: 'NO',
        totalGoals: 'UNDER',
        exactScore: '0-0',
        scorerIds: [],
        redCard: null,
        homeCards: null,
        awayCards: null,
        homeCorners: null,
        awayCorners: null,
        status: 'VOID'
    };
}
/** Did the card's 1X2 anchor land? */
export function anchorHit(p, r) {
    return r.outcome === p.outcome;
}
/** Did the "anytime scorer" leg's backed player score (a no-show simply misses)? A
 *  result settled before the market existed has no `scorerIds`, so it reads as a miss. */
export function isScorerHit(playerId, r) {
    return (r.scorerIds ?? []).includes(playerId);
}
/**
 * Score one card against a settled result. The 1X2 anchor is the qualifier:
 *   wrong → −BASE_POINTS;  right → round( BASE_POINTS × Π legFactor ).
 * A correct leg multiplies up by its multiplier; a wrong leg bites down by its
 * inverse (CONFIG.COMBO.WRONG_LEG_EXPONENT); an ungradeable leg is excluded (×1).
 * The caller must exclude void fixtures (this assumes a non-void SETTLED result).
 */
export function cardScore(p, r) {
    if (!anchorHit(p, r))
        return -CONFIG.COMBO.BASE_POINTS;
    let score = CONFIG.COMBO.BASE_POINTS;
    for (const leg of p.legs ?? [])
        score *= gradeLeg(leg, r).factor;
    return Math.round(score);
}
/**
 * Score a full entry against settled results. Void fixtures (and any fixture without
 * a result) contribute 0 and are excluded. Idempotent and pure.
 */
export function scoreEntry(entry, results) {
    let roundScore = 0;
    let correctAnchors = 0;
    let correctLegs = 0;
    let legsAttempted = 0;
    let cardsPlayed = 0;
    let exactHits = 0;
    let scorerHits = 0;
    const scoreByFixture = {};
    for (const p of entry.predictions) {
        const r = results.get(p.fixtureId);
        if (!r || r.status === 'VOID')
            continue; // void cards excluded
        cardsPlayed++;
        if (anchorHit(p, r))
            correctAnchors++;
        for (const leg of p.legs ?? []) {
            const g = gradeLeg(leg, r);
            if (!g.gradeable)
                continue; // excluded — neither attempted nor wrong
            legsAttempted++;
            if (g.hit) {
                correctLegs++;
                if (leg.marketId === 'exactScore')
                    exactHits++;
                if (leg.marketId === 'scorer')
                    scorerHits++;
            }
        }
        const s = cardScore(p, r);
        scoreByFixture[p.fixtureId] = s;
        roundScore += s;
    }
    return { roundScore, scoreByFixture, correctAnchors, correctLegs, legsAttempted, cardsPlayed, exactHits, scorerHits };
}
//# sourceMappingURL=scoring.js.map